import { ReactNode } from "react";

import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { cn } from "@/common/utils/css.utils";
import { Heading } from "@/ui/cva/Heading";
import { Helper } from "@/ui/typography/Helper";

type ProductDeliveryOptionItemRowProps = {
  /**
   * Delivery option slug for the UI flow tests: one of pickup, courier,
   * brenger, custom, postal-wuunder, postal-sendcloud. Carries both
   * `pdp.shipping.option.<method>` on the row and
   * `pdp.shipping.price.<method>` on the amount, so the two can never drift
   * apart. See whoppah/keur contracts/testids.json.
   */
  method: string;
  /** Fully-styled 24px icon (caller sets size/color). */
  icon: ReactNode;
  title: string;
  subtitle?: ReactNode;
  /** Right-aligned price node, e.g. "from €234*" or "FREE". */
  priceLabel: ReactNode;
  /** Renders the price in the brand colour (used for FREE). */
  isFree?: boolean;
  /** Masks the price with a skeleton while shipping details are loading. */
  loading?: boolean;
  /** When set, shows the inline info (i) icon that opens a details modal. */
  onInfoClick?: () => void;
};

/**
 * Split text into everything-up-to-the-last-word and the last word, so the
 * last word can wrap together with a trailing inline icon (never orphaning it).
 */
const splitLastWord = (text: string): [head: string, tail: string] => {
  const lastSpaceIndex = text.lastIndexOf(" ");

  return lastSpaceIndex === -1
    ? ["", text]
    : [text.slice(0, lastSpaceIndex + 1), text.slice(lastSpaceIndex + 1)];
};

/**
 * Shared layout for a single delivery option row: icon, title (with an optional
 * inline info icon), subtitle and a right-aligned price.
 */
export const ProductDeliveryOptionItemRow = ({
  method,
  icon,
  title,
  subtitle,
  priceLabel,
  isFree,
  loading,
  onInfoClick,
}: ProductDeliveryOptionItemRowProps) => {
  const [titleHead, titleTail] = splitLastWord(title);

  return (
    <div
      data-testid={`pdp.shipping.option.${method}`}
      className="grid grid-cols-[auto_1fr_auto] gap-x-3 p-4"
    >
      {icon}
      <div className="flex flex-col">
        <Heading as="span" size="h5">
          {titleHead}
          <Heading as="span" size="h5" className="whitespace-nowrap">
            {titleTail}
            {onInfoClick && (
              <button
                type="button"
                className="ml-1 inline-flex align-middle"
                onClick={onInfoClick}
              >
                <IconStateInformation className="size-6 shrink-0 text-grey-500" />
              </button>
            )}
          </Heading>
        </Heading>
        {subtitle && <Helper className="text-grey-800">{subtitle}</Helper>}
      </div>
      {loading ? (
        <span className="skeleton h-6 w-16 self-start rounded" />
      ) : (
        <Heading
          as="span"
          size="h5"
          data-testid={`pdp.shipping.price.${method}`}
          className={cn(
            "self-start text-right",
            isFree && "uppercase text-brand-500"
          )}
        >
          {priceLabel}
        </Heading>
      )}
    </div>
  );
};
